import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

type AppMemberRole = "respondent" | "reviewer" | "org_admin";
type AppRole = "admin" | "reviewer" | "respondent";

interface EditUserRolesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
  currentRoles: string[];
  currentMemberRole: string | null;
  currentOrgId: string | null;
  onRolesUpdated?: () => void;
}

export const EditUserRolesModal = ({
  open,
  onOpenChange,
  userId,
  userEmail,
  currentRoles,
  currentMemberRole,
  currentOrgId,
  onRolesUpdated
}: EditUserRolesModalProps) => {
  const [memberRole, setMemberRole] = useState<AppMemberRole>("respondent");
  const [globalRoles, setGlobalRoles] = useState<AppRole[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const availableGlobalRoles: AppRole[] = ["admin", "reviewer", "respondent"];

  // Initialize form when modal opens or props change
  useEffect(() => {
    if (open) {
      setMemberRole((currentMemberRole as AppMemberRole) || "respondent");
      setGlobalRoles(currentRoles as AppRole[]);
    }
  }, [open, currentMemberRole, currentRoles]);

  const toggleGlobalRole = (role: AppRole) => {
    setGlobalRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  // Check for role conflicts
  const hasConflict = () => {
    const conflictingPairs = [
      { global: 'reviewer', org: 'respondent' },
      { global: 'respondent', org: 'reviewer' }
    ];
    
    return conflictingPairs.some(pair => 
      globalRoles.includes(pair.global as AppRole) && memberRole === pair.org
    );
  };

  const handleSubmit = async () => {
    if (!currentOrgId) {
      toast({
        title: "Error",
        description: "User must be assigned to an organization",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Use validated RPC instead of direct table writes
      const { error: rpcError } = await (supabase as any).rpc('admin_update_user_roles', {
        p_user_id: userId,
        p_org_id: currentOrgId,
        p_member_role: memberRole,
        p_global_roles: globalRoles,
        p_reason: 'Role update via Admin Console'
      });

      if (rpcError) throw rpcError;

      toast({
        title: "Roles updated",
        description: `Roles updated successfully for ${userEmail}`
      });

      onOpenChange(false);
      onRolesUpdated?.();
    } catch (error: any) {
      console.error('Error updating roles:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update roles",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Roles for {userEmail}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {hasConflict() && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Role Conflict:</strong> Global roles override organization roles. 
                This user will be treated as their highest global role (e.g., Reviewer) 
                even if their org role is Respondent.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="memberRole">Organization Role *</Label>
            <Select 
              value={memberRole} 
              onValueChange={(value) => setMemberRole(value as AppMemberRole)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="respondent">Respondent</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="org_admin">Organization Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Primary role within the organization
            </p>
          </div>

          <div className="space-y-2">
            <Label>Additional Global Roles</Label>
            <div className="grid grid-cols-2 gap-2">
              {availableGlobalRoles.map(role => (
                <div key={role} className="flex items-center space-x-2">
                  <Checkbox
                    id={`role-${role}`}
                    checked={globalRoles.includes(role)}
                    onCheckedChange={() => toggleGlobalRole(role)}
                  />
                  <Label 
                    htmlFor={`role-${role}`} 
                    className="text-sm font-normal cursor-pointer"
                  >
                    {role.replace('_', ' ')}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Platform-wide permissions in addition to organization role
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
